import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/hooks/use-auth";
import "@/i18n";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { SmoothScroll } from "@/components/SmoothScroll";
import { IntercomProvider } from "@/components/IntercomProvider";
import * as Sentry from "@sentry/react";



function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">

      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">

      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try again or head home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "VecRzvnf87U0NUPSHzlF6zIQ5v7JEchDp1QfQhZyxIA" },
      { title: "Lumvio — Client portal & Payment Protection for freelancers" },
      { name: "description", content: "Lumvio is the identity-verified client portal for freelancers: manage projects, chat with clients, send invoices and get paid through Payment Protection on every milestone." },
      { property: "og:title", content: "Lumvio — Client portal & Payment Protection for freelancers" },
      { property: "og:description", content: "Lumvio is the identity-verified client portal for freelancers: manage projects, chat with clients, send invoices and get paid through Payment Protection on every milestone." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Lumvio" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Cairo:wght@400;500;600;700&display=swap&subset=latin,arabic" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Runs before React hydration to sync <html> class/dir with the user's stored
// theme + language preferences. Prevents hydration mismatches when the SSR
// output ships with the default (light / LTR) but the client remembers dark
// or Arabic. Keep this inline and dependency-free.
const PRE_HYDRATE_SCRIPT = `(function(){try{var d=document.documentElement;var t=localStorage.getItem('lumvio-theme');if(t!=='dark'&&t!=='light'){t=(window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches)?'dark':'light';}d.classList.toggle('dark',t==='dark');var l=localStorage.getItem('lumvio.lang');if(l!=='ar'&&l!=='en'){l=(navigator.language||'en').toLowerCase().indexOf('ar')===0?'ar':'en';}d.lang=l;d.dir=l==='ar'?'rtl':'ltr';}catch(e){}})();`;

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: PRE_HYDRATE_SCRIPT }} />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>

    </html>
  );
}


function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="flex min-h-dvh items-center justify-center bg-background px-4">
          <div className="max-w-md text-center">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              The error has been reported. You can try again.
            </p>
            <button
              onClick={resetError}
              className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Try again
            </button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <SmoothScroll />
            <IntercomProvider />
            <Outlet />
            <Toaster />
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

